import { useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Loader2, Plus, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { RadioSegments } from "@/components/ui/radio-segments";
import { Stepper } from "@/components/ui/stepper";
import { parseDecimal } from "@/lib/decimal";
import { REGIONS } from "./lib/format";

// backend/training/serializers.py — the check-in's scales and choices.
const FEELINGS = [
  { key: "sleep", label: "Sleep", low: "poor", high: "great" },
  { key: "energy", label: "Energy", low: "poor", high: "great" },
  { key: "motivation", label: "Motivation", low: "poor", high: "great" },
  { key: "stress", label: "Stress", low: "calm", high: "stressed" },
];
const SORENESS = [
  { value: "none", label: "None" },
  { value: "mild", label: "Mild" },
  { value: "moderate", label: "Moderate" },
  { value: "limiting", label: "Limiting" },
];
const JOINTS = [
  { value: "shoulder", label: "Shoulder" },
  { value: "elbow", label: "Elbow" },
  { value: "wrist", label: "Wrist" },
  { value: "knee", label: "Knee" },
  { value: "hip", label: "Hip" },
  { value: "low_back", label: "Low back" },
];
const SIDES = [
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
  { value: "both", label: "Both" },
];
const PAIN_ANSWERS = [
  { value: "no", label: "No" },
  { value: "yes", label: "Yes" },
];
// The API's cap on joint rows (every joint, each side).
const MAX_JOINT_ROWS = JOINTS.length * 2;
// backend/training/constants.py MIN/MAX_SESSION_MINUTES.
const MIN_MINUTES = 20;
const MAX_MINUTES = 120;
const MINUTES_STEP = 5;

const scale = (low, high) =>
  [1, 2, 3, 4, 5].map((n) => ({
    value: String(n),
    label: String(n),
    ...(n === 1 ? { detail: low } : n === 5 ? { detail: high } : {}),
  }));

/** The form's state from a check-in already given (Change check-in). */
function fromCheckin(checkin) {
  const feel = {};
  for (const { key } of FEELINGS) {
    const value = checkin?.[key];
    if (value !== null && value !== undefined) feel[key] = String(value);
  }
  const soreness = { ...(checkin?.soreness ?? {}) };
  let pain = null;
  let joints = [];
  if (Array.isArray(checkin?.joint_pain)) {
    pain = checkin.joint_pain.length ? "yes" : "no";
    joints = checkin.joint_pain.map((row) => ({ joint: row.joint, side: row.side ?? "both" }));
  }
  return { feel, soreness, pain, joints };
}

/**
 * The check-in (walkthrough 2): how you slept, your energy and motivation
 * (1 poor … 5 great), stress (1 calm … 5 stressed), soreness per region,
 * any joint pain, and the minutes you have. Every question is optional —
 * an unanswered one is left out, not sent as a guess (D12: missing answers
 * hold) — and *Skip* is always on screen.
 *
 * Joint pain has three answers, because the engine reads them differently:
 * unanswered (nothing changes), No (`[]`: joints that hurt on an earlier
 * day count as fine again) and Yes with the joints (`[{joint, side}]`:
 * exercises loading them are left out today).
 *
 * `onSubmit({checkin, minutes})` — `checkin` is null when nothing was
 * answered or the person skipped.
 */
function CheckinForm({ settings, initial = null, onSubmit, busy = false, error = null }) {
  const id = useId();
  const start = fromCheckin(initial?.checkin);
  const [feel, setFeel] = useState(start.feel);
  const [soreness, setSoreness] = useState(start.soreness);
  const [pain, setPain] = useState(start.pain);
  const [joints, setJoints] = useState(start.joints);
  const [minutes, setMinutes] = useState(
    String(initial?.minutes ?? settings?.session_minutes ?? 60),
  );
  const [minutesError, setMinutesError] = useState(null);
  const [jointErrors, setJointErrors] = useState({});

  const answerPain = (value) => {
    setPain(value);
    setJointErrors({});
    if (value === "yes" && joints.length === 0) setJoints([{ joint: "", side: "both" }]);
  };
  const setJoint = (index, change) => {
    setJoints(joints.map((row, i) => (i === index ? { ...row, ...change } : row)));
    if (change.joint) setJointErrors({ ...jointErrors, [index]: undefined });
  };
  const removeJoint = (index) => {
    setJoints(joints.filter((_, i) => i !== index));
    setJointErrors({});
  };

  /** The minutes as a number, or null after saying what to type. */
  const readMinutes = () => {
    const value = parseDecimal(minutes);
    if (value === null || !Number.isInteger(value) || value < MIN_MINUTES || value > MAX_MINUTES) {
      setMinutesError(`Type a whole number of minutes from ${MIN_MINUTES} to ${MAX_MINUTES}.`);
      return null;
    }
    setMinutesError(null);
    return value;
  };

  const buildCheckin = () => {
    const checkin = {};
    for (const { key } of FEELINGS) {
      if (feel[key] !== undefined) checkin[key] = Number(feel[key]);
    }
    if (Object.keys(soreness).length) checkin.soreness = { ...soreness };
    if (pain === "no") checkin.joint_pain = [];
    if (pain === "yes") checkin.joint_pain = joints.map(({ joint, side }) => ({ joint, side }));
    return Object.keys(checkin).length ? checkin : null;
  };

  const submit = (event) => {
    event.preventDefault();
    if (busy) return;
    const value = readMinutes();
    let ok = value !== null;
    if (pain === "yes") {
      const missing = {};
      joints.forEach((row, index) => {
        if (!row.joint) missing[index] = "Pick the joint, or remove this row.";
      });
      setJointErrors(missing);
      if (Object.keys(missing).length) ok = false;
    }
    if (!ok) return;
    onSubmit({ checkin: buildCheckin(), minutes: value });
  };

  const skip = () => {
    if (busy) return;
    const value = readMinutes();
    if (value === null) return;
    onSubmit({ checkin: null, minutes: value });
  };

  /**
   * One question: its label row — with Clear at the right once answered —
   * over its segments (W4). The row keeps a Clear's height before there is
   * one, so answering never moves the questions below; beside the segments,
   * Clear wrapped onto a row of its own on a phone. The visible label is
   * the group's legend's twin (the legend names the group).
   */
  // a1: Clear goes with the answer it clears, so focus moves to the
  // question's first choice (the group's tab stop) instead of <body>.
  const formRef = useRef(null);
  const clearAndRefocus = (key, onClear) => () => {
    onClear();
    setTimeout(() => {
      formRef.current?.querySelector(`[data-question="${key}"] input[type="radio"]:not(:disabled)`)?.focus();
    }, 0);
  };
  const question = ({ key, label, clearLabel, answered, onClear, segments }) => (
    <div key={key} data-question={key} className="flex flex-col">
      <div data-question-row className="flex min-h-11 items-end justify-between gap-2 md:min-h-9">
        <span aria-hidden="true" className="text-sm font-medium">
          {label}
        </span>
        {answered && (
          <Button
            type="button"
            variant="ghost"
            className="-mr-3 h-11 px-3 text-muted-foreground md:h-9"
            aria-label={`Clear ${clearLabel.toLowerCase()}`}
            onClick={clearAndRefocus(key, onClear)}
          >
            Clear
          </Button>
        )}
      </div>
      {segments}
    </div>
  );

  return (
    <form ref={formRef} onSubmit={submit} aria-labelledby={`${id}-title`} noValidate>
      <div className="flex flex-col gap-6 rounded-lg border bg-card p-4 md:p-6">
        <div>
          {/* focused when Today moves here (a1) */}
          <h2
            id={`${id}-title`}
            tabIndex={-1}
            data-phase-heading
            className="m-0 font-display text-xl font-semibold outline-none"
          >
            How are you today?
          </h2>
          <p className="mt-1 mb-0 text-sm text-muted-foreground">
            Every question is optional — answer what you like, or skip. A rough day makes the plan
            lighter; a good one never adds work.
          </p>
        </div>

        <section aria-labelledby={`${id}-feel`} className="flex flex-col gap-4">
          <h3 id={`${id}-feel`} className="m-0 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            How you feel
          </h3>
          {FEELINGS.map(({ key, label, low, high }) =>
            question({
              key,
              label,
              clearLabel: label,
              answered: feel[key] !== undefined,
              onClear: () => {
                const next = { ...feel };
                delete next[key];
                setFeel(next);
              },
              segments: (
                <RadioSegments
                  legend={label}
                  hideLegend
                  options={scale(low, high)}
                  value={feel[key]}
                  onChange={(value) => setFeel({ ...feel, [key]: value })}
                  disabled={busy}
                />
              ),
            }),
          )}
        </section>

        <section aria-labelledby={`${id}-sore`} className="flex flex-col gap-4">
          <div>
            <h3 id={`${id}-sore`} className="m-0 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              Soreness
            </h3>
            <p className="mt-1 mb-0 text-sm text-muted-foreground">
              How sore each area is today. It tells the plan how the last session that trained it
              went — and a sore area gets less today.
            </p>
          </div>
          {REGIONS.map((region) =>
            question({
              key: region.code,
              label: region.label,
              clearLabel: `${region.label} soreness`,
              answered: soreness[region.code] !== undefined,
              onClear: () => {
                const next = { ...soreness };
                delete next[region.code];
                setSoreness(next);
              },
              segments: (
                <RadioSegments
                  legend={region.label}
                  hideLegend
                  options={SORENESS}
                  value={soreness[region.code]}
                  onChange={(value) => setSoreness({ ...soreness, [region.code]: value })}
                  disabled={busy}
                />
              ),
            }),
          )}
        </section>

        <section aria-labelledby={`${id}-pain-title`} className="flex flex-col gap-3">
          <h3 id={`${id}-pain-title`} className="m-0 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Joints
          </h3>
          {question({
            key: "pain",
            label: "Any joint pain?",
            clearLabel: "joint pain answer",
            answered: pain !== null,
            onClear: () => {
              setPain(null);
              setJoints([]);
              setJointErrors({});
            },
            segments: (
              <RadioSegments
                legend="Any joint pain?"
                hideLegend
                options={PAIN_ANSWERS}
                value={pain ?? undefined}
                onChange={answerPain}
                disabled={busy}
              />
            ),
          })}
          <p className="m-0 text-sm text-muted-foreground">
            {pain === null &&
              "Sharp, in a joint, one side, or getting worse counts. Leave it unanswered and nothing changes."}
            {pain === "no" && "Joints that hurt on an earlier day count as fine again."}
            {pain === "yes" && "Exercises that load these joints are left out today."}
          </p>
          {pain === "yes" && (
            <>
              <ol aria-label="Joints that hurt" className="m-0 flex list-none flex-col gap-3 p-0">
                {joints.map((row, index) => {
                  const n = index + 1;
                  const selectId = `${id}-joint-${index}`;
                  const err = jointErrors[index];
                  return (
                    <li key={index} className="flex flex-wrap items-end gap-2 rounded-md border p-3">
                      <div className="flex min-w-36 flex-1 flex-col gap-1.5">
                        <Label htmlFor={selectId}>Joint {n}</Label>
                        <NativeSelect
                          id={selectId}
                          value={row.joint}
                          className="h-11 md:h-9"
                          disabled={busy}
                          aria-invalid={err ? true : undefined}
                          aria-describedby={err ? `${selectId}-error` : undefined}
                          onChange={(event) => setJoint(index, { joint: event.target.value })}
                        >
                          <option value="">Choose a joint…</option>
                          {JOINTS.map((joint) => (
                            <option key={joint.value} value={joint.value}>
                              {joint.label}
                            </option>
                          ))}
                        </NativeSelect>
                        {err && (
                          <p id={`${selectId}-error`} className="m-0 text-sm text-destructive">
                            {err}
                          </p>
                        )}
                      </div>
                      <RadioSegments
                        legend={
                          <>
                            {/* the space outside the span: a name computed from
                                inline children trims inside it ("Sideof") */}
                            Side <span className="sr-only">of joint {n}</span>
                          </>
                        }
                        options={SIDES}
                        value={row.side}
                        onChange={(side) => setJoint(index, { side })}
                        disabled={busy}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-11 md:size-9"
                        aria-label={`Remove joint ${n}`}
                        title={
                          joints.length === 1 ? "Answer No instead to say nothing hurts" : `Remove joint ${n}`
                        }
                        disabled={busy || joints.length === 1}
                        onClick={() => removeJoint(index)}
                      >
                        <X aria-hidden="true" />
                      </Button>
                    </li>
                  );
                })}
              </ol>
              <div>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 md:h-9"
                  disabled={busy || joints.length >= MAX_JOINT_ROWS}
                  onClick={() => setJoints([...joints, { joint: "", side: "both" }])}
                >
                  <Plus aria-hidden="true" />
                  Add another joint
                </Button>
              </div>
            </>
          )}
        </section>

        <section aria-labelledby={`${id}-time`} className="flex flex-col gap-2">
          <h3 id={`${id}-time`} className="m-0 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Time
          </h3>
          <Stepper
            label="Minutes you have"
            value={minutes}
            onChange={(value) => {
              setMinutes(value);
              setMinutesError(null);
            }}
            step={MINUTES_STEP}
            min={MIN_MINUTES}
            max={MAX_MINUTES}
            inputMode="numeric"
            unit="min"
            disabled={busy}
            decreaseLabel="5 minutes less"
            increaseLabel="5 minutes more"
            helper={`${MIN_MINUTES}–${MAX_MINUTES}, warm-up included. The plan fits its sets into this; your usual is ${settings?.session_minutes ?? 60} (Settings).`}
            error={minutesError}
            className="max-w-xs"
          />
        </section>
      </div>

      {error && (
        <Alert variant="destructive" className="mt-4" role="alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Pinned while the questions scroll: Skip is always there. Marked as
          a bottom bar: the global scroll-padding rule keeps focus from
          landing under it (a0, WCAG 2.4.11). */}
      <div
        data-bottom-bar
        className="sticky bottom-0 z-10 -mx-3 mt-4 flex flex-wrap gap-2 border-t bg-background/95 px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur md:static md:mx-0 md:border-0 md:bg-transparent md:px-0 md:backdrop-blur-none">
        <Button
          type="button"
          variant="outline"
          className="h-11 flex-1 md:h-10 md:flex-none"
          disabled={busy}
          onClick={skip}
        >
          Skip the check-in
        </Button>
        <Button type="submit" className="h-11 flex-1 md:h-10 md:flex-none" disabled={busy}>
          {busy && <Loader2 className="animate-spin" aria-hidden="true" />}
          {busy ? "Planning…" : "See today's plan"}
        </Button>
      </div>
      <p className="mt-2 mb-0 text-sm text-foreground/80">
        Next: today&apos;s plan with the reason for every number. Nothing is saved until you start
        it.
      </p>
    </form>
  );
}

CheckinForm.propTypes = {
  /** TrainingSettings — for the usual session minutes. */
  settings: PropTypes.object,
  /** Answers already given (going back from the plan): `{checkin, minutes}`. */
  initial: PropTypes.shape({
    checkin: PropTypes.object,
    minutes: PropTypes.number,
  }),
  /** Called with `{checkin, minutes}` — checkin null when skipped or empty. */
  onSubmit: PropTypes.func.isRequired,
  /** The plan is being worked out (the buttons wait). */
  busy: PropTypes.bool,
  /** Why the last plan request failed (the answers stay). */
  error: PropTypes.string,
};

export default CheckinForm;
